import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, X } from "lucide-react";
import { PAYMENT_METHODS, gbp } from "@/utils/quoteStages";

const methodLabel = (v) => PAYMENT_METHODS.find((m) => m.value === v)?.label || v;
const today = () => new Date().toISOString().slice(0, 10);

export const QuotePayments = ({ quote, isAdmin, busy, onAdd, onRemove }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", date: today(), method: "bank_transfer", reference: "" });
  const canRecord = isAdmin && quote.balance > 0 && !["declined", "expired", "refunded"].includes(quote.status);
  const payments = quote.payments_received || [];

  const submit = async (e) => {
    e.preventDefault();
    const ok = await onAdd({ ...form, amount: parseFloat(form.amount) || 0 });
    if (ok) { setOpen(false); setForm({ amount: "", date: today(), method: "bank_transfer", reference: "" }); }
  };

  return (
    <div className="rounded-md border border-slate-100 bg-slate-50/60 p-2 space-y-2" data-testid={`quote-money-${quote.id}`}>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div><p className="text-muted-foreground">Total{quote.vat_applicable ? " (inc VAT)" : ""}</p><p className="font-semibold" data-testid={`quote-total-${quote.id}`}>{gbp(quote.total)}</p></div>
        <div><p className="text-muted-foreground">Paid</p><p className="font-semibold text-green-700" data-testid={`quote-paid-${quote.id}`}>{gbp(quote.amount_paid)}</p></div>
        <div><p className="text-muted-foreground">Balance</p><p className={`font-semibold ${quote.balance > 0 ? "text-amber-700" : "text-slate-500"}`} data-testid={`quote-balance-${quote.id}`}>{gbp(quote.balance)}</p></div>
      </div>
      {payments.length > 0 && (
        <ul className="space-y-1">
          {payments.map((p) => (
            <li key={p.id} className="flex items-center justify-between text-[11px] bg-white border border-slate-100 rounded px-2 py-1" data-testid={`quote-payment-${p.id}`}>
              <span><strong>{gbp(p.amount)}</strong> · {methodLabel(p.method)} · {p.date}{p.reference && <> · <span className="text-muted-foreground">{p.reference}</span></>}</span>
              {isAdmin && quote.status !== "refunded" && <button type="button" onClick={() => onRemove(p.id)} disabled={busy} className="p-0.5 rounded hover:bg-red-50 text-red-400" title="Remove payment" data-testid={`quote-payment-remove-${p.id}`}><X size={11} /></button>}
            </li>
          ))}
        </ul>
      )}
      {canRecord && !open && (
        <Button size="sm" variant="outline" className="h-6 text-[11px] gap-1" onClick={() => setOpen(true)} data-testid={`quote-record-payment-${quote.id}`}><Plus size={10} /> Record payment</Button>
      )}
      {open && (
        <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-5 gap-1.5 items-end" data-testid={`quote-payment-form-${quote.id}`}>
          <Input type="number" step="0.01" min="0.01" max={quote.balance} placeholder={`Up to ${gbp(quote.balance)}`} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-7 text-xs" required data-testid="quote-payment-amount" />
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="h-7 text-xs" data-testid="quote-payment-date" />
          <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="h-7 rounded-md border border-border bg-white px-1 text-xs" data-testid="quote-payment-method">
            {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <Input placeholder="Reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="h-7 text-xs" data-testid="quote-payment-reference" />
          <div className="flex gap-1">
            <Button type="submit" size="sm" disabled={busy} className="h-7 text-[11px] bg-slate-900 hover:bg-slate-800 text-white" data-testid="quote-payment-save">Save</Button>
            <Button type="button" size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
          <button type="button" className="col-span-2 md:col-span-5 text-left text-[10px] text-sky-700 hover:underline" onClick={() => setForm({ ...form, amount: String(quote.balance) })} data-testid="quote-payment-fill-balance">Fill full balance ({gbp(quote.balance)})</button>
        </form>
      )}
    </div>
  );
};
